export const ProjectCard = ({
  project,
  variant = "standard",
  onToggleDetails,
}) => {
  const {
    title,
    category,
    description,
    image,
    tags = [],
    github,
    projectUrl,
    details,
  } = project;

  const destination = projectUrl || github;
  const hasDetails = Boolean(details);

  return (
    <article
      className={`project-card-wrapper ${hasDetails ? "clickable" : ""}`}
      onClick={hasDetails ? onToggleDetails : undefined}
      onKeyDown={
        hasDetails
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggleDetails();
              }
            }
          : undefined
      }
      role={hasDetails ? "button" : undefined}
      tabIndex={hasDetails ? 0 : undefined}
    >
      <div className={`project-card ${variant}`}>
        <div className="project-card-image">
          <img src={image} alt={`${title} project`} />
          <div className="project-card-image-overlay" />
        </div>

        <div className="project-card-content">
          <span className="project-card-category">{category}</span>

          <h4>{title}</h4>

          <p>{description}</p>

          {tags.length > 0 && (
            <div className="project-tags">
              {tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          )}

          <div className="project-card-actions">
            {hasDetails && (
              <span className="project-card-link project-details-button">
                View Details ↓
              </span>
            )}

            {!hasDetails && destination && (
              <a
                className="project-card-link"
                href={destination}
                target={projectUrl ? undefined : "_blank"}
                rel={projectUrl ? undefined : "noreferrer"}
              >
                {projectUrl ? "View Project" : "View on GitHub"} →
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export const ProjectDetails = ({ project, onClose }) => {
  const { title, details } = project;

  if (!details) {
    return null;
  }

  const { intro, sections = [] } = details;

  return (
    <div className="project-details">
      <div className="project-details-header">
        <div>
          <span className="project-details-label">PROJECT DETAILS</span>
          <h3>{title}</h3>
        </div>

        <button
          type="button"
          className="project-details-close"
          onClick={onClose}
          aria-label={`Return from ${title} to projects`}
        >
          <span className="project-details-back-desktop">
            ← Back to Projects
          </span>

          <span className="project-details-back-mobile">← Back</span>
        </button>
      </div>

      <div className="project-details-main">
        {intro && (
          <section className="project-detail-section">
            <h4>Overview</h4>
            <p>{intro}</p>
          </section>
        )}

        {sections.map((section, index) => (
          <ProjectDetailSection
            key={`${section.title || section.type}-${index}`}
            section={section}
          />
        ))}
      </div>
    </div>
  );
};

const ProjectDetailSection = ({ section }) => {
  const {
    type,
    title,
    text,
    items = [],
    image,
    alt,
    caption,
    images = [],
    url,
    label,
    sublabel,
  } = section;

  switch (type) {
    case "text":
      return (
        <DetailSection title={title}>{text && <p>{text}</p>}</DetailSection>
      );

    case "bullets":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          {items.length > 0 && (
            <ul className="project-detail-list">
              {items.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          )}
        </DetailSection>
      );

    case "tags":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <div className="project-hardware-list">
            {items.map((item, index) => (
              <span className="project-hardware-chip" key={`${item}-${index}`}>
                {item}
              </span>
            ))}
          </div>
        </DetailSection>
      );

    case "image":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <div className="project-visual-frame">
            <img src={image} alt={alt || title || ""} />
          </div>

          {caption && <p className="project-image-caption">{caption}</p>}
        </DetailSection>
      );

    case "architecture":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <div className="project-architecture">
            {section.columns.map((column, columnIndex) => (
              <div className="project-architecture-stage" key={column.title}>
                <div className="project-architecture-stage-header">
                  <h5>{column.title}</h5>

                  {column.subtitle && <span>{column.subtitle}</span>}
                </div>

                <div className="project-architecture-nodes">
                  {column.nodes.map((node) => (
                    <div
                      className={`project-architecture-node ${
                        node.primary ? "primary" : ""
                      }`}
                      key={node.title}
                    >
                      <strong>{node.title}</strong>
                      <span>{node.text}</span>
                    </div>
                  ))}
                </div>

                {columnIndex < section.columns.length - 1 && (
                  <div
                    className="project-architecture-arrow"
                    aria-hidden="true"
                  >
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </DetailSection>
      );

    case "grid":
      return (
        <section className="project-detail-section">
          <div className="project-detail-grid">
            {items.map((group, index) => (
              <div
                className="project-detail-grid-item"
                key={`${group.title}-${index}`}
              >
                <h4>{group.title}</h4>

                <ul className="project-detail-list">
                  {group.items.map((item, itemIndex) => (
                    <li key={`${item}-${itemIndex}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      );

    case "gallery":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <div className="project-detail-gallery">
            {images.map((item, index) => (
              <figure
                className={`project-detail-gallery-item ${
                  item.featured ? "featured" : ""
                }`}
                key={`${item.alt || "image"}-${index}`}
              >
                <img
                  src={item.image}
                  alt={item.alt || ""}
                  className="project-detail-gallery-image"
                />

                {item.caption && <figcaption>{item.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </DetailSection>
      );

    case "pipeline":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <div className="project-pipeline">
            {items.map((item, index) => (
              <div className="project-pipeline-step" key={`${item}-${index}`}>
                <span className="project-pipeline-node">{item}</span>

                {index < items.length - 1 && (
                  <span className="project-pipeline-arrow" aria-hidden="true">
                    →
                  </span>
                )}
              </div>
            ))}
          </div>
        </DetailSection>
      );

    case "video":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <video
            className="project-video"
            controls
            playsInline
            preload="metadata"
          >
            <source src={url} />
            Your browser does not support HTML video.
          </video>
        </DetailSection>
      );

    case "videos":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <div className="project-video-grid">
            {items.map((video, index) => (
              <div
                className="project-video-item"
                key={`${video.title}-${index}`}
              >
                {video.local ? (
                  <video
                    className="project-video"
                    controls
                    preload="metadata"
                    playsInline
                  >
                    <source src={video.url} type="video/mp4" />
                  </video>
                ) : (
                  <iframe
                    className="project-video"
                    src={video.url}
                    title={video.title}
                    allowFullScreen
                  />
                )}

                <span className="project-video-title">{video.title}</span>
              </div>
            ))}
          </div>
        </DetailSection>
      );

    case "links":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <div className="project-repo-grid">
            {items.map((item, index) => (
              <ProjectLink key={`${item.label}-${index}`} link={item} />
            ))}
          </div>
        </DetailSection>
      );

    case "document":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          <a
            className="project-report-link"
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            <div className="project-report-icon">PDF</div>

            <div className="project-report-content">
              <strong>{label}</strong>
              {sublabel && <span>{sublabel}</span>}
            </div>

            <span className="project-report-arrow">↗</span>
          </a>
        </DetailSection>
      );

    case "columns":
      return (
        <section className="project-detail-section">
          <div className="project-detail-columns">
            {section.columns.map((column, index) => (
              <div
                className="project-detail-column"
                key={`${column.title}-${index}`}
              >
                <h4>{column.title}</h4>

                {column.image && (
                  <img
                    className="project-detail-column-image"
                    src={column.image}
                    alt={column.alt || column.title}
                  />
                )}

                {column.text && <p>{column.text}</p>}
              </div>
            ))}
          </div>
        </section>
      );

    case "projectShowcase":
      return (
        <DetailSection title={title}>
          {text && <p>{text}</p>}

          {section.tags?.length > 0 && (
            <div className="project-tags project-showcase-tags">
              {section.tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          )}

          {section.images?.length > 0 &&
            (section.scrollGallery ? (
              <div className="project-scroll-gallery">
                {section.images.map((image, index) => (
                  <figure
                    className="project-scroll-gallery-item"
                    key={`${title}-${index}`}
                  >
                    <img src={image} alt={`${title} screenshot ${index + 1}`} />
                  </figure>
                ))}
              </div>
            ) : (
              <div className="project-detail-gallery">
                {section.images.map((image, index) => (
                  <figure
                    className="project-detail-gallery-item"
                    key={`${title}-${index}`}
                  >
                    <img
                      className="project-detail-gallery-image"
                      src={image}
                      alt={`${title} screenshot ${index + 1}`}
                    />
                  </figure>
                ))}
              </div>
            ))}
        </DetailSection>
      );

    default:
      return null;
  }
};

const DetailSection = ({ title, children }) => (
  <section className="project-detail-section">
    {title && <h4>{title}</h4>}
    {children}
  </section>
);

const ProjectLink = ({ link }) => {
  const { label, sublabel, url, download = false, icon = "</>" } = link;

  return (
    <a
      className="project-repo-card"
      href={url}
      target={download ? undefined : "_blank"}
      rel={download ? undefined : "noreferrer"}
      download={download || undefined}
    >
      <div className="project-repo-icon">{download ? "↓" : icon}</div>

      <div className="project-repo-content">
        <strong>{label}</strong>
        {sublabel && <span>{sublabel}</span>}
      </div>

      <span className="project-repo-arrow">{download ? "↓" : "↗"}</span>
    </a>
  );
};
